// Tile projectile that flies from matched tile position to enemy
// Visual: colored circle with trail that impacts the enemy

import Phaser from 'phaser';
import { ElementType } from '@/shared/types/common';

/** Element color mapping for projectiles */
const ELEMENT_COLORS: Record<ElementType, number> = {
  [ElementType.RED]: 0xe05545,
  [ElementType.BLUE]: 0x5890e0,
  [ElementType.GREEN]: 0x52c07a,
  [ElementType.YELLOW]: 0xe6b832,
  [ElementType.PURPLE]: 0x9b6ad8,
};

const PROJECTILE_SIZE = 16;
const TRAIL_PARTICLE_COUNT = 5;

export class TileProjectile extends Phaser.GameObjects.Container {
  private core: Phaser.GameObjects.Graphics;
  private glow: Phaser.GameObjects.Graphics;
  private trail: Phaser.GameObjects.Graphics[] = [];
  private color: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    public element: ElementType,
  ) {
    super(scene, x, y);

    this.color = ELEMENT_COLORS[element] ?? 0xffffff;

    // Outer glow
    this.glow = scene.add.graphics();
    this.glow.fillStyle(this.color, 0.3);
    this.glow.fillCircle(0, 0, PROJECTILE_SIZE + 6);
    this.add(this.glow);

    // Core circle
    this.core = scene.add.graphics();
    this.core.fillStyle(this.color, 1);
    this.core.fillCircle(0, 0, PROJECTILE_SIZE / 2);
    // Inner highlight
    this.core.fillStyle(0xffffff, 0.5);
    this.core.fillCircle(-2, -2, PROJECTILE_SIZE / 4);
    this.add(this.core);

    // Create trail particles
    for (let i = 0; i < TRAIL_PARTICLE_COUNT; i++) {
      const trailPart = scene.add.graphics();
      const alpha = 0.4 - i * 0.07;
      const size = PROJECTILE_SIZE / 2 - i * 2;
      trailPart.fillStyle(this.color, alpha);
      trailPart.fillCircle(0, 0, Math.max(2, size));
      trailPart.setVisible(false);
      this.trail.push(trailPart);
      scene.add.existing(trailPart);
    }

    this.setDepth(100);
    scene.add.existing(this);
  }

  /**
   * Animate the projectile flying to the target position.
   * Returns a promise that resolves when the animation completes.
   */
  flyTo(targetX: number, targetY: number, duration = 250): Promise<void> {
    return new Promise((resolve) => {
      const trailPositions: { x: number; y: number }[] = [];

      this.scene.tweens.add({
        targets: this,
        x: targetX,
        y: targetY,
        duration,
        ease: 'Sine.easeIn',
        onUpdate: () => {
          // Update trail positions (history of previous positions)
          trailPositions.unshift({ x: this.x, y: this.y });
          if (trailPositions.length > TRAIL_PARTICLE_COUNT) {
            trailPositions.pop();
          }

          // Position trail particles along the path
          for (let i = 0; i < this.trail.length; i++) {
            const trailPart = this.trail[i]!;
            const pos = trailPositions[i + 1]; // Offset by 1 to trail behind
            if (pos) {
              trailPart.setPosition(pos.x, pos.y);
              trailPart.setVisible(true);
            }
          }

          // Pulsing glow effect
          const pulse = 1 + Math.sin(Date.now() * 0.02) * 0.15;
          this.glow.setScale(pulse);
        },
        onComplete: () => {
          // Clean up trail
          for (const trailPart of this.trail) {
            trailPart.destroy();
          }
          this.trail = [];
          this.destroy();
          resolve();
        },
      });
    });
  }

  /** Get the color of this projectile */
  getColor(): number {
    return this.color;
  }
}

/**
 * Spawn a projectile from source position and animate it to target.
 * Returns a promise that resolves when the projectile hits.
 */
export function spawnProjectile(
  scene: Phaser.Scene,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  element: ElementType,
  delay = 0,
): Promise<{ element: ElementType; color: number }> {
  return new Promise((resolve) => {
    scene.time.delayedCall(delay, () => {
      const projectile = new TileProjectile(scene, fromX, fromY, element);
      const color = projectile.getColor(); // capture before flyTo destroys the object
      projectile.flyTo(toX, toY, 200).then(() => {
        resolve({ element, color });
      });
    });
  });
}
